import {
  UserIPCookie,
  api,
  getCurrentURL,
  isNumber,
  logger,
  store,
} from "../shared";
import { IEventLoggerEnum, IEventLoggerModel } from "../types";

/**
 * Batched logs will be sent at the following times:
 *
 * - Before page unloads
 * - When queue has more than 10 posts
 * - 5 minutes has elapsed since last POST call and user is still active on page and
 *     there are logs to send
 *
 *
 */

interface IEventLoggerModuleState {
  batchedPosts: IEventLoggerModel[];
  lastBatchedProcessed: null | Date;
}

const logEndpoint = process.env.LOGGING_API;

export const EventLoggerModule = (() => {
  const FIVE_MIN = 5 * 60 * 1000;

  const state: IEventLoggerModuleState = {
    batchedPosts: [],
    lastBatchedProcessed: null,
  };

  const clearBatchedPosts = () => {
    state.batchedPosts.length = 0;
  };

  const queueEventPost = (eventPost: IEventLoggerModel) => {
    const { batchedPosts, lastBatchedProcessed } = state;
    batchedPosts.push(eventPost);

    const batchedPostsLength = batchedPosts.length;

    const lastProcessedWasMoreThan5MinutesAgo = lastBatchedProcessed
      ? new Date().getTime() - lastBatchedProcessed.getTime() > FIVE_MIN
      : false;

    const hasMoreThan10BatchedEvents = batchedPostsLength > 10;

    const shouldSendBatchedRequests =
      lastProcessedWasMoreThan5MinutesAgo || hasMoreThan10BatchedEvents;

    if (shouldSendBatchedRequests) {
      postEventLogsToAPI();
    }
  };

  const postEventLogsToAPI = async () => {
    const { batchedPosts } = state;

    if (batchedPosts.length) {
      const requestBody = {
        eventLogs: batchedPosts,
      };

      let responseWasSuccess = false;

      try {
        await api.post({
          url: logEndpoint,
          requestBody,
        });
        responseWasSuccess = true;
      } catch (error) {
        logger.error({
          message: "Failed to post to bating cage script log endpoint",
          error,
        });
      }

      responseWasSuccess && clearBatchedPosts();
      state.lastBatchedProcessed = new Date();
    }
  };

  let defaultEventLoggerObject: Pick<
    IEventLoggerModel,
    | "internalSessionId"
    | "ipAddress"
    | "userAgent"
    | "clusterVersionId"
    | "experimentVariationId"
  > | null = null;

  const getDefaultEventLoggerObject = () => {
    if (defaultEventLoggerObject) return defaultEventLoggerObject;

    const internalSessionId = store.get("internalSessionId") || 0;
    const ipAddress = UserIPCookie.get() || "";

    const isPreviewMode = store.get("isPreviewMode");
    const previewModeParameters = store.get("previewModeParameters");

    defaultEventLoggerObject = {
      internalSessionId,
      ipAddress,
      userAgent: navigator.userAgent,
      ...(isPreviewMode && {
        isPreviewMode: true,
        ...(isNumber(previewModeParameters.previewClusterVersionId) && {
          clusterVersionId: parseInt(
            previewModeParameters.previewClusterVersionId as string
          ),
        }),
        ...(isNumber(previewModeParameters.previewExperimentVariationId) && {
          experimentVariationId: parseInt(
            previewModeParameters.previewExperimentVariationId as string
          ),
        }),
      }),
    };

    return defaultEventLoggerObject;
  };

  const getBaseEventLoggingModel = () =>
    defaultEventLoggerObject || getDefaultEventLoggerObject();

  const logEvent = ({
    eventType,
    message,
    payload,
  }: {
    eventType: IEventLoggerEnum;
    message?: IEventLoggerModel["message"];
    payload?: IEventLoggerModel["payload"];
  }) => {
    if (process.env.NODE_ENV !== "production") return;

    const baseEventLoggingModel = getBaseEventLoggingModel();

    const eventPost: IEventLoggerModel = {
      ...baseEventLoggingModel,
      createdDate: new Date().toISOString(),
      domainPageUrl: getCurrentURL(),
      eventType,
      message: message || null,
      payload: payload || null,
    };

    queueEventPost(eventPost);
  };

  return {
    logEvent,
    postEventLogsToAPI,
  };
})();
